import { ReactNode, useCallback, useState } from 'react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { JsonValue } from '@backstage/types';
import {
  scaffolderApiRef,
  ScaffolderFieldExtensions,
  useCustomFieldExtensions,
  useCustomLayouts,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import { EmbeddableWorkflow } from '@backstage/plugin-scaffolder-react/alpha';
import { SelectUsersFieldExtension } from '../../../scaffolder/SelectUsers';
import { UploadFilesExtension } from '../../../scaffolder/UploadFiles';
import { AdHocJobTaskProgress } from './AdHocJobTaskProgress';

export type AdHobJobWorkflowProps = {
  templateName: string;
};

const fieldExtensions = (
  <ScaffolderFieldExtensions>
    <SelectUsersFieldExtension />
    <UploadFilesExtension />
  </ScaffolderFieldExtensions>
);

export function AdHocJobWorkflow({ templateName }: AdHobJobWorkflowProps): ReactNode | null {
  const scaffolderApi = useApi(scaffolderApiRef);
  const { secrets } = useTemplateSecrets();
  const extensions = useCustomFieldExtensions(fieldExtensions);
  const layouts = useCustomLayouts(fieldExtensions);
  const [taskId, setTaskId] = useState<string>();

  const onCreate = useCallback(
    async (values: Record<string, JsonValue>) => {
      const templateRef = stringifyEntityRef({ kind: 'Template', namespace: 'default', name: templateName });
      const { taskId: createdTaskId } = await scaffolderApi.scaffold({ templateRef, values, secrets });
      setTaskId(createdTaskId);
    },
    [scaffolderApi, secrets, templateName],
  );

  const onError = (error: Error | undefined) => <h2>An error occurred while running the job. {error?.message}</h2>;

  if (taskId) {
    return <AdHocJobTaskProgress taskId={taskId} />;
  }

  return (
    <EmbeddableWorkflow
      namespace="default"
      templateName={templateName}
      onCreate={onCreate}
      onError={onError}
      extensions={extensions}
      layouts={layouts}
    />
  );
}
